// imports de bibliotecas
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Tour from 'reactour';
import { ErrorMessage } from '@hookform/error-message';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody,
  Button
} from 'reactstrap';

// imports de componentes do sistema
import FlexSelect from '../../../../components/Inputs/FlexSelect';
import FormTooltip from '../../../../components/Inputs/FormTooltip';
import SimpleHeader from '../../../../components/Headers/SimpleHeader.js';
import ReactLoader from '../../../../components/Loader';
import { buttonStyle } from '../../../../components/Containers/ConfigurationContainer/components/utils';

// imports de componentes da matriz
import { matrixSLATourSteps } from '../../utils/matrixSLATourSteps';
import MatrixSLATable from '../components/MatrixSLATable';
import DaySchedule from '../components/DaySchedule';
import HolidaysTable from '../components/matrixSLAHolidays/components/MatrixSLAHolidaysTable';
import ServiceClassificationTable from '../components/serviceClassifications/components/ServiceClassificationTable';
import { MatrixSLAScheduleContainer } from '../form/styled';

// services
import MatricesSLAService from '../service';
import CustomerPortfolioService from '../../CustomerPortfolio/service';

const MatrixSLAForm = () => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [matrixSLATourOpen, setMatrixSLATourOpen] = useState(false);
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  const [isSubmiting, setIsSubmiting] = useState(false);

  const [customerPortfolios, setCustomerPortfolios] = useState([]);
  const [currentCustomerPortfolioId, setCurrentCustomerPortfolioId] =
    useState(null);

  const [matrixSLA, setMatrixSLA] = useState(null);
  const [defaultValuesSLA, setDefaultValuesSLA] = useState(null);
  const [idOfNewlyCreatedSlaMatrix, setIdOfNewlyCreatedSlaMatrix] =
    useState(null);

  const [showDisabledsun, setShowDisablesun] = useState(false);
  const [showDisabledmon, setShowDisablemon] = useState(false);
  const [showDisabledtue, setShowDisabletue] = useState(false);
  const [showDisabledwed, setShowDisablewed] = useState(false);
  const [showDisabledthu, setShowDisablethu] = useState(false);
  const [showDisabledfri, setShowDisablefri] = useState(false);
  const [showDisabledsat, setShowDisablesat] = useState(false);

  // constantes
  const DAYS_OF_THE_WEEK = {
    sunday: {
      showDisabled: showDisabledsun,
      setShowDisabled: setShowDisablesun,
      portugueseName: 'Domingo'
    },
    monday: {
      showDisabled: showDisabledmon,
      setShowDisabled: setShowDisablemon,
      portugueseName: 'Segunda-feira'
    },
    tuesday: {
      showDisabled: showDisabledtue,
      setShowDisabled: setShowDisabletue,
      portugueseName: 'Terça-feira'
    },
    wednesday: {
      showDisabled: showDisabledwed,
      setShowDisabled: setShowDisablewed,
      portugueseName: 'Quarta-feira'
    },
    thursday: {
      showDisabled: showDisabledthu,
      setShowDisabled: setShowDisablethu,
      portugueseName: 'Quinta-feira'
    },
    friday: {
      showDisabled: showDisabledfri,
      setShowDisabled: setShowDisablefri,
      portugueseName: 'Sexta-feira'
    },
    saturday: {
      showDisabled: showDisabledsat,
      setShowDisabled: setShowDisablesat,
      portugueseName: 'Sábado'
    }
  };

  const defaultValues = {
    rows: [
      { name: 'incidente', values: [] },
      { name: 'problema', values: [] },
      { name: 'mudança', values: [] },
      { name: 'serviço', values: [] }
    ],
    columns: [
      { name: 'leve', values: [] },
      { name: 'medio', values: [] },
      { name: 'grave', values: [] }
    ]
  };

  // react hook form
  const { handleSubmit, control, setValue, errors, watch, reset } = useForm({
    defaultValues
  });

  // watch values do react hook form
  const selectedCustomerPortfolio = watch('customerPortfolioId');

  // useEffects
  useEffect(() => {
    let isMounted = true;

    CustomerPortfolioService.getWithoutPagination(false, true)
      .then((response) => {
        const data = response.data.data.content.map((item) => ({
          name: item.name,
          id: item.id
        }));
        if (currentCustomerPortfolioId) {
          data.push(currentCustomerPortfolioId);
        }

        if (isMounted) {
          setCustomerPortfolios(data);
        }
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            'Erro ao buscar categoria dos clientes'
        );
      });

    return () => {
      isMounted = false;
    };
  }, [currentCustomerPortfolioId]);

  useEffect(() => {
    if (matrixSLA && matrixSLA.customerPortfolio) {
      CustomerPortfolioService.get(matrixSLA.customerPortfolio.id)
        .then((res) => {
          const getCustomerPortfolio = res.data.content;
          setCurrentCustomerPortfolioId(getCustomerPortfolio);
        })
        .catch((err) => {
          const message = err.data.message;
          toast.error(message || 'Erro ao listar categoria do cliente!', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    }
  }, [matrixSLA]);

  useEffect(() => {
    if (matrixSLA && matrixSLA.impacts) {
      const mappedData = matrixSLA.impacts.map((impact) => {
        if (impact && impact.criticality) {
          return {
            id: impact.id,
            name: impact.name,
            values: impact.criticality.map((crit) => {
              return {
                id: crit.id,
                sla_service: crit.value.sla_service,
                sla_solution: crit.value.sla_solution
              };
            })
          };
        }
      });

      const initialValues = {};
      mappedData.forEach((row, rowIndex) => {
        if (row && row.values) {
          row.values.forEach((value, columnIndex) => {
            initialValues[`sla_service[${rowIndex}][${columnIndex}]`] =
              value.sla_service;
            initialValues[`sla_solution[${rowIndex}][${columnIndex}]`] =
              value.sla_solution;
          });
        }
      });

      setDefaultValuesSLA(initialValues);
    }
  }, [matrixSLA]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      MatricesSLAService.getMatrixSLAById(id)
        .then((response) => {
          const data = response.data.content;
          const openingHoursSLA = data.openingHoursSLA;
          let isSequencialTimerActivated = data.isSequencialTimerActivated
            ? 'isSequencialTimerActivatedYes'
            : 'isSequencialTimerActivatedNo';
          if (!data) return;
          const newData = {
            ...data,
            ...openingHoursSLA,
            [isSequencialTimerActivated]: true,
            rows: data.impacts.map((impact) => ({
              name: impact.name,
              values: []
            })),
            columns: data.impacts[0].criticality.map((crit) => ({
              name: crit.name,
              values: []
            }))
          };
          reset({
            ...newData,
            customerPortfolioId: data.customerPortfolio.id
          });
          const daySetters = {
            sunday: setShowDisablesun,
            monday: setShowDisablemon,
            tuesday: setShowDisabletue,
            wednesday: setShowDisablewed,
            thursday: setShowDisablethu,
            friday: setShowDisablefri,
            saturday: setShowDisablesat
          };
          Object.entries(openingHoursSLA).forEach(([day, value]) => {
            if (daySetters[day]) {
              daySetters[day](value);
            }
          });
          setMatrixSLA(data);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(
            err.response?.data?.message ||
              err.message ||
              'Erro ao buscar configuração da Matriz SLA'
          );
        });
    }
  }, [id, reset, setValue]);

  // react hook form field array
  const {
    fields: rowFields,
    append: appendRow,
    remove: removeRow
  } = useFieldArray({
    control,
    name: 'rows'
  });

  const {
    fields: columnFields,
    append: appendColumn,
    remove: removeColumn
  } = useFieldArray({
    control,
    name: 'columns'
  });

  // funções
  const transformDataToCreate = (data) => {
    const {
      columns,
      rows,
      sla_solution,
      sla_service,
      name,
      customerPortfolioId,
      isSequencialTimerActivatedYes,
      defaultSlaSolution,
      defaultSlaService
    } = data;

    const impacts = rows.map((row, index) => {
      const criticality = columns.map((column, colIndex) => {
        return {
          name: column.name,
          value: {
            sla_service: parseInt(sla_service[index][colIndex]),
            sla_solution: parseInt(sla_solution[index][colIndex])
          }
        };
      });

      return {
        name: row.name,
        criticality
      };
    });

    return {
      name,
      isSequencialTimerActivated: isSequencialTimerActivatedYes ? true : false,
      enabled: true,
      defaultSlaService: parseInt(defaultSlaService),
      defaultSlaSolution: parseInt(defaultSlaSolution),
      customerPortfolioId,
      impacts
    };
  };

  const validateData = (data) => {
    if (
      data.sla_solution.some((row) => row.some((value) => !value)) ||
      data.sla_service.some((row) => row.some((value) => !value))
    ) {
      toast.error('Preencha todos os campos de SLA', {
        autoClose: 3000,
        closeOnClick: true
      });

      return false;
    }

    const hasAtLeastOneDayEnabled = [
      showDisabledsun,
      showDisabledmon,
      showDisabledtue,
      showDisabledwed,
      showDisabledthu,
      showDisabledfri,
      showDisabledsat
    ].some(Boolean);

    if (!hasAtLeastOneDayEnabled) {
      toast.error('A matriz SLA não possui nenhum dia da semana habilitado', {
        autoClose: 3000,
        closeOnClick: true
      });

      return false;
    }

    return true;
  };

  const formatDataForEditing = (matrixSLA, data) => {
    const {
      columns,
      rows,
      name,
      customerPortfolioId,
      isSequencialTimerActivatedYes,
      defaultSlaSolution,
      defaultSlaService
    } = data;

    const impacts = matrixSLA.impacts.map(({ id, slaMatrixId }, index) => ({
      name: rows[index].name,
      id,
      slaMatrixId
    }));

    const criticality = matrixSLA.impacts[0].criticality.map(
      ({ id, slaMatrixId }, index) => ({
        name: columns[index].name,
        id,
        slaMatrixId
      })
    );

    return {
      id: matrixSLA.id,
      name,
      isSequencialTimerActivated: Boolean(isSequencialTimerActivatedYes),
      enabled: true,
      defaultSlaService: parseInt(defaultSlaService),
      defaultSlaSolution: parseInt(defaultSlaSolution),
      customerPortfolioId,
      impacts,
      criticality
    };
  };

  const createOpeningHoursSLA = (data) => ({
    sunday: showDisabledsun,
    monday: showDisabledmon,
    tuesday: showDisabledtue,
    wednesday: showDisabledwed,
    thursday: showDisabledthu,
    friday: showDisabledfri,
    saturday: showDisabledsat,
    beginTimeMonday: data.begin_time_monday,
    endTimeMonday: data.end_time_monday,
    beginTimeTuesday: data.begin_time_tuesday,
    endTimeTuesday: data.end_time_tuesday,
    beginTimeWednesday: data.begin_time_wednesday,
    endTimeWednesday: data.end_time_wednesday,
    beginTimeThursday: data.begin_time_thursday,
    endTimeThursday: data.end_time_thursday,
    beginTimeFriday: data.begin_time_friday,
    endTimeFriday: data.end_time_friday,
    beginTimeSaturday: data.begin_time_saturday,
    endTimeSaturday: data.end_time_saturday,
    beginTimeSunday: data.begin_time_sunday,
    endTimeSunday: data.end_time_sunday
  });

  const onSubmit = async (data) => {
    if (!validateData(data)) {
      return;
    }

    const openingHoursSLA = createOpeningHoursSLA(data);

    let dataToSubmit = id
      ? formatDataForEditing(matrixSLA, data)
      : transformDataToCreate(data);

    dataToSubmit = { ...dataToSubmit, openingHoursSLA };

    const usrLogin = localStorage.getItem('login');
    setIsSubmiting(true);
    try {
      if (id) {
        await MatricesSLAService.updateMatrixSLA(id, dataToSubmit, usrLogin)
          .then((response) => {
            toast.success(
              response.data.message || 'Matriz SLA atualizada com sucesso!'
            );
            history.push('/admin/matrix-sla');
          })
          .catch((err) => {
            toast.error(
              err.response?.data?.message ||
                err.message ||
                'Erro ao atualizar a Matriz SLA'
            );
          });
      } else {
        await MatricesSLAService.createMatrixSLA(dataToSubmit, usrLogin)
          .then((response) => {
            const createdSLAMatrixId = response.data.content.matrixSLAId;
            setIdOfNewlyCreatedSlaMatrix(createdSLAMatrixId);
            changeWizardStep(1);
            toast.success(
              response.data.message || 'Matriz SLA criada com sucesso!'
            );
          })
          .catch((err) => {
            toast.error(
              err.response?.data?.message ||
                err.message ||
                'Erro ao criar a Matriz SLA'
            );
          });
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          'Erro ao enviar a requisição'
      );
    } finally {
      setIsSubmiting(false);
    }
  };

  // funções utilitárias
  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/matrix-sla');
  };

  const openMatrixSLATour = () => {
    setMatrixSLATourOpen(true);
  };

  const closeMatrixSLATour = () => {
    setMatrixSLATourOpen(false);
  };

  const changeWizardStep = (step) => {
    setCurrentWizardStep(step);
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/matrix-sla"
        buttonTitle="Voltar para a pagina de listagem de Matrizes SLA"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-3">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">
                    {currentWizardStep === 0
                      ? 'Configuração da Matriz SLA'
                      : currentWizardStep === 1
                      ? 'Feriados da Matriz SLA'
                      : 'Classificações de Serviço da Matriz SLA'}
                  </h3>
                  {selectedCustomerPortfolio && (
                    <>
                      <Button
                        className="float-right "
                        type="button"
                        outline
                        color="info"
                        onClick={openMatrixSLATour}
                        title="Saiba mais sobre as configurações da Matriz SLA"
                      >
                        Saiba Mais
                      </Button>
                      <Tour
                        steps={matrixSLATourSteps}
                        isOpen={matrixSLATourOpen}
                        onRequestClose={closeMatrixSLATour}
                      />
                    </>
                  )}
                </CardHeader>
                <CardBody>
                  {(() => {
                    switch (currentWizardStep) {
                      case 0:
                        return (
                          <>
                            {loading ? (
                              <Row className="justify-content-md-center">
                                <ReactLoader />
                              </Row>
                            ) : (
                              <Form
                                className="needs-validation"
                                onSubmit={handleSubmit(onSubmit, (err) => {
                                  console.log(err);
                                })}
                              >
                                <Row>
                                  <Col sm="6" className="mb-3 tour1-dt">
                                    <label className="form-control-label text-capitalize">
                                      Nome da Matriz SLA*
                                    </label>
                                    <Controller
                                      control={control}
                                      name="name"
                                      defaultValue=""
                                      render={(props) => (
                                        <Input
                                          type="text"
                                          placeholder="Nome da Matriz SLA"
                                          {...props}
                                        />
                                      )}
                                      rules={{
                                        required:
                                          'O nome da matriz SLA é obrigatório!'
                                      }}
                                    />
                                    <ErrorMessage
                                      errors={errors}
                                      name="name"
                                      render={({ message }) =>
                                        errorFormMessage(message)
                                      }
                                    />
                                  </Col>
                                  <Col sm="6" className="mb-3 tour2-dt">
                                    <label className="form-control-label text-capitalize">
                                      Categorias de Clientes Disponiveis*{' '}
                                      <FormTooltip text="Lista todas as categorias que não possuem uma matriz SLA associada." />
                                    </label>
                                    <Controller
                                      control={control}
                                      name="customerPortfolioId"
                                      defaultValue=""
                                      render={(props) => (
                                        <FlexSelect
                                          isClearable={true}
                                          dataOptions={customerPortfolios || []}
                                          isMulti={false}
                                          closeMenuOnSelect={true}
                                          value={props.value}
                                          valueController={setValue}
                                          fieldName="customerPortfolioId"
                                          labelName="name"
                                          valueName="id"
                                          defaultValue={
                                            matrixSLA?.customerPortfolio?.id ||
                                            ''
                                          }
                                        />
                                      )}
                                      rules={{
                                        required:
                                          'A categoria do cliente é obrigatório!'
                                      }}
                                    />
                                    <ErrorMessage
                                      errors={errors}
                                      name="customerPortfolioId"
                                      render={({ message }) =>
                                        errorFormMessage(message)
                                      }
                                    />
                                  </Col>
                                </Row>
                                {selectedCustomerPortfolio && (
                                  <>
                                    <hr />
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                      }}
                                    >
                                      <Row className="pt-0 mt-0 mb-2">
                                        <Col md="5">
                                          <label className="form-control-label">
                                            Configurar Matriz
                                          </label>
                                        </Col>
                                      </Row>
                                      <MatrixSLATable
                                        columnFields={columnFields}
                                        control={control}
                                        id={id}
                                        appendColumn={appendColumn}
                                        removeColumn={removeColumn}
                                        rowFields={rowFields}
                                        appendRow={appendRow}
                                        removeRow={removeRow}
                                        setValue={setValue}
                                        defaultValuesSLA={defaultValuesSLA}
                                      />
                                      <hr />
                                      <Row className="tour9-dt mb-3">
                                        <Col md="3">
                                          <label className="form-control-label">
                                            Valor padrão de SLA de Atendimento*
                                          </label>
                                          <Controller
                                            control={control}
                                            as={
                                              <Input
                                                placeholder="Valor padrão de serviço"
                                                type="number"
                                                min="1"
                                              />
                                            }
                                            setValue={setValue}
                                            name={'defaultSlaService'}
                                            rules={{
                                              required:
                                                'O valor padrão do SLA de Atendimento é obrigatório!'
                                            }}
                                            defaultValue={defaultValuesSLA}
                                          />
                                          <ErrorMessage
                                            errors={errors}
                                            name="defaultSlaService"
                                            render={({ message }) =>
                                              errorFormMessage(message)
                                            }
                                          />
                                        </Col>
                                        <Col md="3">
                                          <label className="form-control-label">
                                            Valor padrão do SLA de Solução*
                                          </label>
                                          <Controller
                                            control={control}
                                            as={
                                              <Input
                                                placeholder="Valor padrão de solução"
                                                type="number"
                                                min="1"
                                              />
                                            }
                                            setValue={setValue}
                                            name="defaultSlaSolution"
                                            rules={{
                                              required:
                                                'O valor padrão do SLA de Solução é obrigatório!'
                                            }}
                                            defaultValue={defaultValuesSLA}
                                          />
                                          <ErrorMessage
                                            errors={errors}
                                            name="defaultSlaSolution"
                                            render={({ message }) =>
                                              errorFormMessage(message)
                                            }
                                          />
                                        </Col>
                                      </Row>
                                      <hr />
                                      <Row className="pt-0 mt-0 mb-2">
                                        <Col md="5">
                                          <label className="form-control-label">
                                            Configuração de Horário de
                                            funcionamento da Matriz
                                          </label>
                                        </Col>
                                      </Row>
                                      <Row
                                        className="tour10-dt mb-5"
                                        style={{
                                          textAlign: 'center',
                                          overflowX: 'auto'
                                        }}
                                      >
                                        <MatrixSLAScheduleContainer>
                                          <Col className="col-sm">
                                            <h5
                                              style={{
                                                marginTop: '2.7rem',
                                                fontSize: '0.800rem',
                                                fontWeight: '600',
                                                color: '#525f7f'
                                              }}
                                            >
                                              Horário inicial:
                                            </h5>
                                            <h5
                                              style={{
                                                marginTop: '3rem',
                                                fontSize: '0.800rem',
                                                fontWeight: '600',
                                                color: '#525f7f'
                                              }}
                                            >
                                              Horário final:
                                            </h5>
                                          </Col>
                                          {Object.keys(DAYS_OF_THE_WEEK).map(
                                            (day) => (
                                              <DaySchedule
                                                key={day}
                                                day={day}
                                                showDisabled={
                                                  DAYS_OF_THE_WEEK[day]
                                                    .showDisabled
                                                }
                                                setShowDisabled={
                                                  DAYS_OF_THE_WEEK[day]
                                                    .setShowDisabled
                                                }
                                                control={control}
                                                errors={errors}
                                                errorFormMessage={
                                                  errorFormMessage
                                                }
                                                setValue={setValue}
                                                portugueseName={
                                                  DAYS_OF_THE_WEEK[day]
                                                    .portugueseName
                                                }
                                              />
                                            )
                                          )}
                                        </MatrixSLAScheduleContainer>
                                      </Row>
                                      <Row className="tour11-dt pt-0 mt-0">
                                        <Col md="5">
                                          <label className="form-control-label">
                                            Ativar Timer Sequencial*
                                          </label>
                                        </Col>
                                      </Row>
                                      <Row className="pt-0 mt-0">
                                        <Col md="5" className="ml-5">
                                          <Controller
                                            name={
                                              'isSequencialTimerActivatedYes'
                                            }
                                            control={control}
                                            defaultValue={false}
                                            render={(props) => {
                                              return (
                                                <>
                                                  <label className="form-control-label">
                                                    <Input
                                                      type="checkbox"
                                                      onChange={(e) => {
                                                        if (!props.value) {
                                                          props.onChange(
                                                            e.target.checked
                                                          );
                                                          if (
                                                            e.target.checked
                                                          ) {
                                                            setValue(
                                                              'isSequencialTimerActivatedNo',
                                                              false
                                                            );
                                                          }
                                                        }
                                                      }}
                                                      checked={props.value}
                                                    />
                                                    Sim
                                                  </label>
                                                </>
                                              );
                                            }}
                                          />
                                        </Col>
                                      </Row>
                                      <Row className="pt-0 mt-0 mb-4">
                                        <Col md="5" className="ml-5">
                                          <Controller
                                            name={
                                              'isSequencialTimerActivatedNo'
                                            }
                                            control={control}
                                            defaultValue={id ? false : true}
                                            render={(props) => {
                                              return (
                                                <>
                                                  <label className="form-control-label">
                                                    <Input
                                                      type="checkbox"
                                                      onChange={(e) => {
                                                        if (!props.value) {
                                                          props.onChange(
                                                            e.target.checked
                                                          );
                                                          if (
                                                            e.target.checked
                                                          ) {
                                                            setValue(
                                                              'isSequencialTimerActivatedYes',
                                                              false
                                                            );
                                                          }
                                                        }
                                                      }}
                                                      checked={props.value}
                                                    />
                                                    Não
                                                  </label>
                                                </>
                                              );
                                            }}
                                          />
                                        </Col>
                                      </Row>
                                    </div>
                                  </>
                                )}
                                <hr />
                              </Form>
                            )}
                          </>
                        );
                      case 1:
                        return !id && idOfNewlyCreatedSlaMatrix ? (
                          <>
                            <HolidaysTable
                              matrixSLAId={idOfNewlyCreatedSlaMatrix}
                            />
                            <hr />
                          </>
                        ) : null;
                      case 2:
                        return !id && idOfNewlyCreatedSlaMatrix ? (
                          <>
                            <ServiceClassificationTable
                              matrixSLAId={idOfNewlyCreatedSlaMatrix}
                            />
                            <hr />
                          </>
                        ) : null;
                      default:
                        return null;
                    }
                  })()}{' '}
                  <Button
                    color="danger"
                    onClick={
                      currentWizardStep === 0
                        ? cancelAction
                        : () => changeWizardStep(currentWizardStep - 1)
                    }
                  >
                    {
                      {
                        0: 'Cancelar',
                        1: 'Cancelar',
                        2: 'Voltar'
                      }[currentWizardStep]
                    }
                  </Button>
                  <Button
                    style={buttonStyle(systemConfiguration.primary_color)}
                    onClick={
                      currentWizardStep === 0
                        ? handleSubmit(onSubmit, (err) => {
                            console.log(err);
                          })
                        : currentWizardStep === 1
                        ? () => changeWizardStep(2)
                        : () => cancelAction()
                    }
                    disabled={isSubmiting}
                  >
                    {id
                      ? 'Atualizar Matriz SLA'
                      : {
                          0: 'Continuar e Salvar',
                          1: 'Continuar e Salvar',
                          2: 'Salvar e Concluir'
                        }[currentWizardStep]}
                  </Button>
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default MatrixSLAForm;
